/* GVS MX1F – Direct MX hosted contact endpoint; no storefront API key. */
(() => {
  'use strict';
  if (window.__gvsMxFormsHandler) return;
  window.__gvsMxFormsHandler = true;

  const selector = 'form[data-gvs-mx-form]';
  const defaults = {
    sending: 'Sending…',
    error: 'Your enquiry could not be sent. Your details are still here. Please try again or contact us directly.',
    uncertain: 'We could not confirm whether your enquiry was received. Please contact us before resending to avoid a duplicate enquiry.',
    reference: 'Your reference:',
    success: 'Thank you. Your enquiry has been sent.'
  };

  function showMessage(element, message) {
    if (!element) return;
    element.textContent = message;
    element.hidden = false;
    element.focus();
  }

  function readResult(result) {
    const data = result && (result.data || result);
    const ticket = data && data.ticket;
    const reference = data && (data.ticket_code || data.ticketCode || (ticket && ticket.code) || data.code || '');
    const success = Boolean((result && result.success === true) || (data && (data.success === true || data.status === 'success')));
    const rejected = Boolean((result && result.success === false) || (data && (data.success === false || data.status === 'error' || data.error)));
    return { success, rejected, reference: typeof reference === 'string' ? reference : '' };
  }

  function getSubmitButton(form) {
    return form.querySelector('button[type="submit"], input[type="submit"]');
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(selector)) return;
    event.preventDefault();
    if (form.dataset.mxState) return;
    if (!form.reportValidity()) return;

    const source = new FormData(form);
    const payload = new FormData();
    const sourceLabel = String(source.get('contact[Form source]') || 'Website Enquiry');
    const enquiryType = String(source.get('contact[Enquiry type]') || '');
    const customerMessage = String(source.get('details') || '').trim();
    const details = ['Enquiry Channel: GVS Website Enquiry'];
    const contextFields = [];
    const successNotice = form.querySelector('[data-gvs-mx-success]');
    const successText = form.querySelector('[data-gvs-mx-success-text]');
    const errorNotice = form.querySelector('[data-gvs-mx-error]');
    const submitButton = getSubmitButton(form);
    if (!submitButton || !successNotice || !successText || !errorNotice) return;

    payload.set('shop', String(source.get('shop') || ''));
    payload.set('eml', String(source.get('eml') || ''));
    payload.set('name', String(source.get('name') || ''));
    payload.set('question', enquiryType ? `${sourceLabel} - ${enquiryType}` : sourceLabel);
    payload.set('email', String(source.get('email') || ''));

    for (const [key, value] of source.entries()) {
      if (!key.startsWith('contact[') || !key.endsWith(']') || typeof value !== 'string' || !value.trim()) continue;
      const label = key.slice(8, -1);
      const valueText = value.trim();
      contextFields.push([key, valueText]);
      details.push(`${label}: ${valueText}`);
    }

    // MX has not yet documented hosted-endpoint custom-field names.
    // Default mode retains all context inside ticket details; never silently discard it.
    // Vendor-confirmed mapping can be tested in an unpublished theme using
    // data-gvs-mx-context-mode="dual-write" and JSON data-gvs-mx-context-field-map.
    const mode = form.dataset.gvsMxContextMode || 'legacy';
    let allContextMapped = false;
    if (mode !== 'legacy' && form.dataset.gvsMxContextFieldMap) {
      try {
        const fieldMap = JSON.parse(form.dataset.gvsMxContextFieldMap);
        const reserved = new Set(['shop', 'eml', 'name', 'question', 'details', 'email']);
        if (fieldMap && typeof fieldMap === 'object' && !Array.isArray(fieldMap)) {
          allContextMapped = contextFields.every(([key]) => typeof fieldMap[key] === 'string' && fieldMap[key].trim() && !reserved.has(fieldMap[key].trim()));
          if (allContextMapped) {
            for (const [key, value] of contextFields) payload.set(fieldMap[key].trim(), value);
          }
        }
      } catch (_error) { /* Keep the complete original message on any mapping error. */ }
    }
    // Separation must be explicitly enabled only after MX confirms a working map.
    if (mode === 'separated' && form.dataset.gvsMxContextVerified === 'true' && allContextMapped && contextFields.length) {
      payload.set('details', customerMessage || 'Website enquiry submitted.');
    } else {
      details.push(`\nMessage:\n${customerMessage || 'Website enquiry submitted.'}`);
      payload.set('details', details.join('\n'));
    }

    successNotice.hidden = true;
    errorNotice.hidden = true;
    form.dataset.mxState = 'sending';
    form.setAttribute('aria-busy', 'true');
    submitButton.disabled = true;
    const buttonLabel = submitButton.querySelector(':scope > span') || submitButton;
    const initialLabel = buttonLabel.textContent;
    buttonLabel.textContent = form.dataset.sendingMessage || defaults.sending;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 30000);

    try {
      const response = await fetch(form.action, {
        method: 'POST', body: payload, credentials: 'omit',
        headers: { Accept: 'application/json' }, signal: controller.signal
      });
      const result = readResult(await response.json());
      if (response.ok && result.success && !result.rejected) {
        form.dataset.mxState = 'sent';
        const reference = result.reference ? ` ${form.dataset.ticketLabel || defaults.reference} ${result.reference}` : '';
        successText.textContent = `${form.dataset.successMessage || defaults.success}${reference}`;
        successNotice.hidden = false;
        successNotice.focus();
        return;
      }
      if (result.rejected || (response.status >= 400 && response.status < 500)) {
        delete form.dataset.mxState;
        submitButton.disabled = false;
        showMessage(errorNotice, form.dataset.errorMessage || defaults.error);
        return;
      }
      throw new Error('Unconfirmed MX response');
    } catch (_error) {
      form.dataset.mxState = 'unconfirmed';
      showMessage(errorNotice, form.dataset.uncertainMessage || defaults.uncertain);
    } finally {
      window.clearTimeout(timer);
      form.removeAttribute('aria-busy');
      buttonLabel.textContent = initialLabel;
    }
  });

  document.addEventListener('click', (event) => {
    const resetButton = event.target.closest('[data-gvs-mx-reset]');
    if (!resetButton) return;
    const form = resetButton.closest(selector);
    if (!form || form.dataset.mxState !== 'sent') return;
    const persistent = [...form.querySelectorAll('[data-gvs-cff-attribution], [data-gvs-ca-track], [data-gvs-pq-variant], [data-gvs-pq-sku], [data-gvs-pq-variant-id]')]
      .map(field => [field, field.value]);
    form.reset();
    for (const [field, value] of persistent) field.value = value;
    delete form.dataset.mxState;
    form.querySelector('[data-gvs-mx-success]').hidden = true;
    form.querySelector('[data-gvs-mx-error]').hidden = true;
    const context = form.querySelector('[data-gvs-cff-context-message]');
    if (context) context.hidden = true;
    const enquiry = form.querySelector('select[name="contact[Enquiry type]"]');
    if (enquiry) enquiry.dispatchEvent(new Event('change', { bubbles: true }));
    const submitButton = getSubmitButton(form);
    if (submitButton) submitButton.disabled = false;
    form.querySelector('[name="name"]')?.focus();
  });

  // Horizon product option changes must be reflected in the question context.
  document.addEventListener('variant:update', (event) => {
    const variant = event.detail?.resource;
    const productId = String(event.detail?.data?.productId || '');
    if (!variant || !productId) return;
    document.querySelectorAll('form[data-gvs-mx-product-question]').forEach(form => {
      if (form.dataset.gvsPqProductId !== productId) return;
      const fields = [
        ['[data-gvs-pq-variant-id]', variant.id],
        ['[data-gvs-pq-variant]', variant.public_title || variant.title],
        ['[data-gvs-pq-sku]', variant.sku]
      ];
      for (const [selector, value] of fields) {
        const field = form.querySelector(selector);
        if (field) field.value = String(value ?? '');
      }
    });
  });
})();
